<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link class="navbar-brand" to="/">
              <b-img
                :src="!isVerticalMenuCollapsed ? logo : appLogoSmall"
                :height="!isVerticalMenuCollapsed ? 35 : 70"
                class="logo"
                :style="!isVerticalMenuCollapsed ? '' : 'margin-left: -30px'"
              />
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0
        }
      "
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
      <vertical-nav-menu-items
        v-if="
          userData &&
          (userData.role_name === 'establishment_admin' ||
            userData.role_name === 'establishment_presale_clerk' ||
            userData.role_name === 'establishment_supplier')
        "
        :items="establishmentAdminStores"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from "@/navigation/vertical"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BLink, BImg } from "bootstrap-vue"
import { provide, computed, ref } from "@vue/composition-api"
import useAppConfig from "@core/app-config/useAppConfig"
import { $themeConfig } from "@themeConfig"
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue"
import useVerticalNavMenu from "./useVerticalNavMenu"
import logo from "@/assets/images/LogoImages/SVG Vector Files/Transparent Logo_horizontal.svg"

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide("isMouseHovered", isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    )

    // App Name
    const { appName, appLogoImage, appLogoSmall } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appLogoSmall,
      logo,
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData")),
      establishmentAdminStores: [],
    }
  },
  mounted() {
    if (this.userData) {
      const establishmentsMap = new Map()

      this.userData.scoped_roles.forEach((x) => {
        if (
          x.role_resource_type === "Establishment" &&
          this.userData.role_name === "establishment_admin" &&
          x.role_name === "establishment_admin"
        ) {
          establishmentsMap.set(x.role_resource_name, x)
        } else if (
          this.userData.role_name === "establishment_presale_clerk" &&
          x.role_resource_type === "Establishment" &&
          x.role_name === "establishment_presale_clerk"
        ) {
          establishmentsMap.set(x.role_resource_name, x)
        } else if (
          this.userData.role_name === "establishment_supplier" &&
          x.role_resource_type === "Establishment" &&
          x.role_name === "establishment_admin"
        ) {
          establishmentsMap.set(x.role_resource_name, x)
        }
      })

      this.establishmentAdminStores = Array.from(
        establishmentsMap.values()
      ).map((x) => ({
        title: x.role_resource_name,
        route: {
          name: "establishment-view",
          params: { id: x.role_resource_id },
        },
        icon: "ShoppingBagIcon",
        roles: [
          "establishment_admin",
          "establishment_presale_clerk",
          "establishment_supplier",
        ],
      }))
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>

<style lang="scss" scoped>
.logo {
  //filter: invert(0.2);
  transition: filter 0.3s ease;
}

.dark-layout {
  .logo {
    filter: none;
  }
}
</style>
